import { useEffect, useRef, useState } from "react";
import api, { apiError, fileUrl, inr } from "../../lib/api";
import { toast } from "sonner";
import { AccountEmailSelect } from "../../components/admin/AccountEmailSelect";
import {
  Wallet, Plus, Pencil, RefreshCw, Trash2, CheckCircle2, QrCode, X, Loader2,
  AlertTriangle, Eye, CreditCard,
} from "lucide-react";

const SLOTS = [1, 2, 3, 4];

export default function AccountSettings() {
  const [accounts, setAccounts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSlot, setEditSlot] = useState(null); // slot number for account form
  const [manageId, setManageId] = useState(null); // account id for QR manage
  const [confirm, setConfirm] = useState(null); // {title, message, onYes, danger, yesLabel}
  const [preview, setPreview] = useState(null); // {qr_image, amount, account_name}
  const [mailboxes, setMailboxes] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/payment-accounts");
      setAccounts(data.accounts || []);
      setActiveId(data.active_payment_account_id || null);
      setMissing(data.missing_amounts || []);
      api.get("/email-verify/config").then(({ data: ev }) => setMailboxes(ev.mailboxes || [])).catch(() => {});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const bySlot = (n) => accounts.find((a) => a.account_slot === n);
  const activeAcc = accounts.find((a) => a.id === activeId);
  const manageAcc = accounts.find((a) => a.id === manageId);

  const setActive = (acc) => setConfirm({
    title: "Switch active payment account?",
    message: `Switch the active payment account to “Account ${acc.account_slot} – ${acc.account_name}”? All payment pages will immediately start using this account's UPI ID and amount-wise QR codes.`,
    yesLabel: "Confirm",
    onYes: async () => {
      try {
        const { data } = await api.post(`/payment-accounts/${acc.id}/activate`);
        toast.success("Active payment account changed successfully");
        setConfirm(null);
        if ((data.missing_amounts || []).length) {
          toast.warning(`This account is missing QR codes for: ${data.missing_amounts.map((a) => inr(a)).join(", ")}`);
        }
        load();
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    },
  });

  const removeAccount = (acc) => setConfirm({
    title: "Delete this account?",
    message: `Remove “Account ${acc.account_slot} – ${acc.account_name}” and all its QR mappings? This cannot be undone.`,
    danger: true, yesLabel: "Delete",
    onYes: async () => {
      try {
        await api.delete(`/payment-accounts/${acc.id}`);
        toast.success("Account deleted");
        setConfirm(null); load();
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    },
  });

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="account-settings-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Wallet className="w-7 h-7 text-blue-600" /> Account Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">Maintain up to 4 payment accounts with amount-wise QR codes. Switch the whole platform to a different account with one tap.</p>
      </div>

      {/* Active account banner */}
      <div className={`rounded-2xl p-5 border-2 ${activeAcc ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="active-account-banner">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Active Payment Account</div>
        {activeAcc ? (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="font-head text-lg font-bold text-slate-900" data-testid="active-account-name">Account {activeAcc.account_slot} — {activeAcc.account_name}</span>
            <span className="font-mono text-sm text-slate-600">{activeAcc.upi_id}</span>
          </div>
        ) : (
          <div className="mt-1 font-head text-lg font-bold text-amber-800" data-testid="no-active-account">No Active Payment Account</div>
        )}
        {activeAcc && missing.length > 0 && (
          <div className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-100/70 rounded-xl px-3 py-2" data-testid="missing-amounts-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>QR codes missing for these page amounts: <b>{missing.map((a) => inr(a)).join(", ")}</b>. Add them in this account so those pages show a QR.</span>
          </div>
        )}
      </div>

      {/* Account cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {SLOTS.map((n) => {
          const acc = bySlot(n);
          const isActive = acc && acc.id === activeId;
          return (
            <div key={n} className={`rounded-2xl border bg-white p-5 ${isActive ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`} data-testid={`account-card-${n}`}>
              <div className="flex items-center justify-between">
                <div className="font-head font-bold text-slate-900">Account {n}</div>
                {acc ? (
                  isActive
                    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full" data-testid={`account-status-${n}`}>● ACTIVE</span>
                    : <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full" data-testid={`account-status-${n}`}>INACTIVE</span>
                ) : (
                  <span className="text-xs font-medium text-slate-400">Not Configured</span>
                )}
              </div>

              {acc ? (
                <div className="mt-3 space-y-1.5 text-sm">
                  <div><span className="text-slate-400">Account Name:</span> <span className="font-medium text-slate-800">{acc.account_name}</span></div>
                  <div><span className="text-slate-400">UPI ID:</span> <span className="font-mono text-slate-800">{acc.upi_id}</span></div>
                  <div><span className="text-slate-400">QR Codes Configured:</span> <span className="font-semibold text-blue-600" data-testid={`account-qrcount-${n}`}>{acc.qr_count}</span></div>
                  <div className="pt-1"><div className="text-slate-400 text-xs mb-1">Verification email (auto verification)</div>
                    <AccountEmailSelect account={acc} mailboxes={mailboxes} onChanged={load} testId={`account-email-select-${n}`} /></div>
                  <div className="flex flex-wrap gap-2 pt-3">
                    <button onClick={() => setManageId(acc.id)} data-testid={`manage-account-${n}`} className="flex items-center gap-1.5 text-sm font-semibold bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800"><CreditCard className="w-4 h-4" /> Manage Account</button>
                    {!isActive && <button onClick={() => setActive(acc)} data-testid={`set-active-${n}`} className="flex items-center gap-1.5 text-sm font-semibold bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4" /> Set as Active</button>}
                    {!isActive && <button onClick={() => removeAccount(acc)} data-testid={`delete-account-${n}`} className="flex items-center gap-1.5 text-sm font-semibold text-red-600 px-2 py-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ) : (
                <div className="mt-6 flex flex-col items-center justify-center py-6 text-center">
                  <QrCode className="w-8 h-8 text-slate-300 mb-2" />
                  <button onClick={() => setEditSlot(n)} data-testid={`add-account-${n}`} className="flex items-center gap-1.5 text-sm font-semibold bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> Add Account</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editSlot != null && (
        <AccountFormModal slot={editSlot} account={bySlot(editSlot)} onClose={() => setEditSlot(null)} onSaved={() => { setEditSlot(null); load(); }} />
      )}
      {manageAcc && (
        <ManageAccountModal account={manageAcc} onClose={() => setManageId(null)} onChange={load} onEditDetails={() => { setManageId(null); setEditSlot(manageAcc.account_slot); }} onPreview={setPreview} />
      )}
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      {preview && <QrPreviewModal {...preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function AccountFormModal({ slot, account, onClose, onSaved }) {
  const [name, setName] = useState(account?.account_name || "");
  const [upi, setUpi] = useState(account?.upi_id || "");
  const [status, setStatus] = useState(account?.status || "inactive");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Account Name is required.");
    if (!upi.trim()) return toast.error("UPI ID is required.");
    setSaving(true);
    try {
      if (account) await api.put(`/payment-accounts/${account.id}`, { account_slot: slot, account_name: name.trim(), upi_id: upi.trim(), status });
      else await api.post(`/payment-accounts`, { account_slot: slot, account_name: name.trim(), upi_id: upi.trim(), status });
      toast.success("Account saved");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testid="account-form-modal">
      <h2 className="font-head text-lg font-bold text-slate-900 mb-4">{account ? `Manage Account ${slot}` : `Add Account ${slot}`}</h2>
      <label className="block text-sm font-medium text-slate-700 mb-1">Account Name <span className="text-red-500">*</span></label>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" placeholder="e.g. GDIOT Main Account" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4" />
      <label className="block text-sm font-medium text-slate-700 mb-1">UPI ID <span className="text-red-500">*</span></label>
      <input value={upi} onChange={(e) => setUpi(e.target.value)} data-testid="account-upi-input" placeholder="e.g. gdiot@bank" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono mb-4" />
      <label className="block text-sm font-medium text-slate-700 mb-1">Account Status</label>
      <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="account-status-select" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2">
        <option value="inactive">Inactive</option>
        <option value="active">Active</option>
      </select>
      <p className="text-xs text-slate-400 mb-5">Only one account can be the global Active Payment Account. Use “Set as Active” on the card to switch.</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="account-save-btn" className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes</button>
      </div>
    </Modal>
  );
}

function ManageAccountModal({ account, onClose, onChange, onEditDetails, onPreview }) {
  const [acc, setAcc] = useState(account);
  const [adding, setAdding] = useState(false);
  const [editMap, setEditMap] = useState(null);
  const [removeMap, setRemoveMap] = useState(null);
  const maps = [...(acc.qr_mappings || [])].sort((a, b) => a.amount - b.amount);

  const refresh = (data) => { setAcc(data); onChange(); };

  const doRemove = async () => {
    try {
      const { data } = await api.delete(`/payment-accounts/${acc.id}/qr/${removeMap.id}`);
      toast.success("QR mapping removed"); setRemoveMap(null); refresh(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal onClose={onClose} testid="manage-account-modal" wide>
      <div className="flex items-start justify-between mb-1">
        <h2 className="font-head text-lg font-bold text-slate-900">Account {acc.account_slot} — {acc.account_name}</h2>
      </div>
      <div className="flex items-center justify-between mb-5 text-sm">
        <span className="font-mono text-slate-600">{acc.upi_id}</span>
        <button onClick={onEditDetails} data-testid="edit-account-details-btn" className="flex items-center gap-1 text-blue-600 font-semibold hover:underline"><Pencil className="w-3.5 h-3.5" /> Edit account details</button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="font-head font-bold text-slate-800">QR Codes by Payment Amount</div>
        <button onClick={() => setAdding(true)} data-testid="add-amount-qr-btn" className="flex items-center gap-1.5 text-sm font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> Add Amount QR</button>
      </div>

      {maps.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-8 border border-dashed border-slate-200 rounded-xl">No QR codes yet. Add an amount-specific QR to get started.</div>
      ) : (
        <div className="space-y-3">
          {maps.map((m) => (
            <div key={m.id} className="flex items-center gap-4 border border-slate-200 rounded-xl p-3" data-testid={`qr-row-${m.amount}`}>
              <button onClick={() => onPreview({ qr_image: m.qr_image, amount: m.amount, account_name: acc.account_name })} className="shrink-0 w-16 h-16 rounded-lg border border-slate-200 bg-white p-1 hover:border-blue-400">
                <img src={fileUrl(m.qr_image)} alt="qr" className="w-full h-full object-contain" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold text-slate-900">{inr(m.amount)}</div>
                {m.qr_label && <div className="text-xs text-slate-500 truncate">{m.qr_label}</div>}
                <div className="text-xs text-emerald-600 font-medium">Active</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onPreview({ qr_image: m.qr_image, amount: m.amount, account_name: acc.account_name })} title="Preview" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Eye className="w-4 h-4" /></button>
                <button onClick={() => setEditMap(m)} data-testid={`qr-edit-${m.amount}`} title="Edit / Replace" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => setRemoveMap(m)} data-testid={`qr-remove-${m.amount}`} title="Remove" className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Done</button>
      </div>

      {(adding || editMap) && (
        <QrFormModal account={acc} mapping={editMap} onClose={() => { setAdding(false); setEditMap(null); }} onSaved={(data) => { setAdding(false); setEditMap(null); refresh(data); }} />
      )}
      {removeMap && (
        <ConfirmModal title="Remove QR code?" message={`Remove the QR code configured for ${inr(removeMap.amount)} from this account?`} danger yesLabel="Remove" onYes={doRemove} onCancel={() => setRemoveMap(null)} />
      )}
    </Modal>
  );
}

function QrFormModal({ account, mapping, onClose, onSaved }) {
  const [amount, setAmount] = useState(mapping?.amount ?? "");
  const [label, setLabel] = useState(mapping?.qr_label || "");
  const [qr, setQr] = useState(mapping?.qr_image || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQr(data.path);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Payment Amount must be a valid positive number.");
    if (!qr) return toast.error("Please upload a QR code image.");
    setSaving(true);
    try {
      let res;
      if (mapping) res = await api.put(`/payment-accounts/${account.id}/qr/${mapping.id}`, { amount: amt, qr_image: qr, qr_label: label });
      else res = await api.post(`/payment-accounts/${account.id}/qr`, { amount: amt, qr_image: qr, qr_label: label });
      toast.success("QR code saved");
      onSaved(res.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testid="qr-form-modal">
      <h2 className="font-head text-lg font-bold text-slate-900 mb-4">{mapping ? `Edit QR — ${inr(mapping.amount)}` : "Add Amount QR"}</h2>
      <label className="block text-sm font-medium text-slate-700 mb-1">Payment Amount <span className="text-red-500">*</span></label>
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="qr-amount-input" placeholder="1499" className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm" />
      </div>
      <label className="block text-sm font-medium text-slate-700 mb-1">QR Code <span className="text-red-500">*</span></label>
      <div className="mb-4">
        {qr ? (
          <div className="relative inline-block">
            <img src={fileUrl(qr)} alt="qr" className="w-40 h-40 object-contain border border-slate-200 rounded-lg bg-white p-1" />
            <button onClick={() => fileRef.current?.click()} className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs bg-slate-900 text-white px-2 py-1 rounded-md">Replace QR</button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} data-testid="qr-upload-btn" className="w-40 h-40 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-blue-400">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><QrCode className="w-6 h-6 mb-1" /><span className="text-xs">Upload QR</span></>}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
      </div>
      <label className="block text-sm font-medium text-slate-700 mb-1">QR Label <span className="text-slate-400 font-normal">(optional, admin only)</span></label>
      <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="qr-label-input" placeholder="e.g. Drone Course QR" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-5" />
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving || uploading} data-testid="qr-save-btn" className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save QR Code</button>
      </div>
    </Modal>
  );
}

function QrPreviewModal({ qr_image, amount, account_name, onClose }) {
  return (
    <Modal onClose={onClose} testid="qr-preview-modal">
      <div className="text-center">
        <div className="font-head text-xl font-bold text-slate-900">{inr(amount)}</div>
        <div className="text-sm text-slate-500 mb-4">{account_name}</div>
        <img src={fileUrl(qr_image)} alt="QR" className="w-64 h-64 max-w-full object-contain mx-auto border border-slate-200 rounded-xl bg-white p-2" />
        <button onClick={onClose} className="mt-5 px-5 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Close</button>
      </div>
    </Modal>
  );
}

function ConfirmModal({ title, message, onYes, onCancel, danger, yesLabel = "Confirm" }) {
  return (
    <Modal onClose={onCancel} testid="confirm-modal">
      <h2 className="font-head text-lg font-bold text-slate-900 mb-2">{title}</h2>
      <p className="text-sm text-slate-600 mb-6">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={onYes} data-testid="confirm-yes-btn" className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>{yesLabel}</button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid={testid} onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} p-5 sm:p-6 my-auto`} onMouseDown={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400"><X className="w-5 h-5" /></button>
        {children}
      </div>
    </div>
  );
}
